// src/pages/quotation/QuotationListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Plus, Eye, Calendar, Clock, Send, CheckCircle, XCircle, CalendarX, CalendarClock, RefreshCw, User, Phone, Store } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getQuotations } from '../../services/api/offlineSales/quotations';
import { getShops } from '../../services/api/shops';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import DocNumberCell from '../procurement/components/DocNumberCell';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './QuotationListPage.module.css';

const STATUS_ICONS = {
  draft: FileText,
  sent: Send,
  accepted: CheckCircle,
  rejected: XCircle,
  expired: CalendarX,
  converted: RefreshCw,
};

// Only shopId/status are actually read by the backend controller
// (rettzy-backend/src/modules/offlineSales/controllers/quotation.controller.js) — no
// search/date-range query params exist server-side yet, so this page only wires those two.
const statusConfig = {
  draft: { color: '#6b7280', bg: '#f3f4f6' },
  sent: { color: '#2563eb', bg: '#dbeafe' },
  accepted: { color: '#10b981', bg: '#d1fae5' },
  rejected: { color: '#ef4444', bg: '#fee2e2' },
  expired: { color: '#f59e0b', bg: '#fef3c7' },
  converted: { color: '#7c3aed', bg: '#ede9fe' },
};

const QuotationListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [quotations, setQuotations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [shops, setShops] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ shopId: '', status: '' });

  useEffect(() => {
    (async () => {
      try {
        const result = await getShops();
        setShops(Array.isArray(result) ? result : result?.shops || []);
      } catch (err) {
        console.error('Failed to load shops:', err);
      }
    })();
  }, []);

  useEffect(() => {
    fetchQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, filters]);

  const fetchQuotations = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };
      if (filters.shopId) params.shopId = filters.shopId;
      if (filters.status) params.status = filters.status;

      const response = await getQuotations(params);
      setQuotations(response.quotations || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch quotations:', err);
      if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('quotation.failedToLoadQuotations'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ shopId: '', status: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const hasActiveFilters = () => Object.values(filters).some(v => v !== '');

  const filterDefinitions = [
    {
      key: 'shopId',
      label: t('quotation.shop'),
      type: 'select',
      placeholder: t('quotation.allShops'),
      options: shops.map(s => ({ value: s.id.toString(), label: s.name })),
    },
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: t('quotation.allStatuses'),
      options: Object.keys(statusConfig).map(key => ({
        value: key,
        label: t(`quotation.status_${key}`),
      })),
    },
  ];

  const columns = [
    {
      key: 'quotationNumber',
      header: t('quotation.quotationNumber'),
      width: '160px',
      render: (row) => <DocNumberCell number={row.quotationNumber} to={`/offline-sales/quotations/${row.id}`} />,
    },
    {
      key: 'customer',
      header: t('quotation.customer'),
      render: (row) => {
        const phone = row.customer?.phone || row.prospectPhone;
        return (
          <div className={tb.partyInfo}>
            <div className={tb.partyName}><User size={14} />{row.customer?.name || row.prospectName || t('common.na')}</div>
            {phone && <div className={tb.partyMeta}><Phone size={12} />{phone}</div>}
          </div>
        );
      },
    },
    {
      key: 'shop',
      header: t('quotation.shop'),
      width: '160px',
      render: (row) => row.Shop?.name
        ? <div className={tb.shopBadge}><Store size={12} />{row.Shop.name}</div>
        : <span className={tb.naText}>—</span>,
    },
    {
      key: 'validUntil',
      header: t('quotation.validUntil'),
      width: '140px',
      render: (row) => row.validUntil
        ? <div className={tb.dateInfo}><CalendarClock size={12} />{formatDate(row.validUntil)}</div>
        : <span className={tb.naText}>—</span>,
    },
    {
      key: 'grandTotal',
      header: t('quotation.grandTotal'),
      width: '140px',
      align: 'right',
      render: (row) => <div className={tb.totalAmount}>{formatCurrency(row.grandTotal)}</div>,
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '130px',
      render: (row) => {
        const cfg = statusConfig[row.status] || statusConfig.draft;
        const StatusIcon = STATUS_ICONS[row.status] || Clock;
        return (
          <div
            className={tb.statusBadge}
            style={{ backgroundColor: cfg.bg, borderColor: cfg.color, color: cfg.color }}
          >
            <StatusIcon size={14} />
            <span>{t(`quotation.status_${row.status}`)}</span>
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      width: '160px',
      render: (row) => (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.createdAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const tableActions = {
    render: (row) => (
      <div className={tb.actionsWrapper}>
        <Link
          to={`/offline-sales/quotations/${row.id}`}
          className={`${tb.actionButton} ${tb.viewButton}`}
          title={t('common.viewDetails')}
        >
          <Eye size={14} />
        </Link>
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.quotationListPage}>
      <PageHeader
        title={t('quotation.allQuotations')}
        description={t('quotation.viewAndManageAllQuotations')}
        icon={FileText}
        actions={
          hasPermission('create_quotation') && (
            <Link to="/offline-sales/quotations/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('quotation.createQuotation')}
            </Link>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={quotations}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/offline-sales/quotations/${row.id}`)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() ? t('quotation.noQuotationsFound') : t('quotation.noQuotationsYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('quotation.noQuotationsFoundDescription')
            : t('quotation.noQuotationsYetDescription')
        }
        emptyIcon={<FileText size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_quotation') ? (
            <Link to="/offline-sales/quotations/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('quotation.createFirstQuotation')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default QuotationListPage;
